/**
 * NoFamilyState Component
 *
 * Shown when a user doesn't have a family associated with their account.
 * Prompts them to create a family or shows appropriate message for admin users.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Home, Users, Plus, AlertCircle } from 'lucide-react';
import { useAuthStore } from '@/core/stores/authStore';

interface NoFamilyStateProps {
  onCreateFamily?: () => void;
}

export function NoFamilyState({ onCreateFamily }: NoFamilyStateProps) {
  const { user } = useAuthStore();
  const [isCreating, setIsCreating] = useState(false);

  const isAdmin = user?.role === 'admin';

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 bg-sage/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <Users className="w-8 h-8 text-sage" />
        </div>

        <h2 className="font-display text-2xl font-semibold text-navy mb-3">
          {isAdmin ? 'No Family Selected' : 'Welcome to BrightPath!'}
        </h2>

        <p className="text-warm-gray mb-6">
          {isAdmin
            ? "You're logged in as an administrator. To access family features, you can create a test family or switch to a family user account."
            : "To get started, create your family profile. This will allow you to manage children, track health information, and access all family features."}
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {onCreateFamily ? (
            <button
              onClick={onCreateFamily}
              disabled={isCreating}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-sage text-white rounded-xl hover:bg-sage-dark transition-colors disabled:opacity-50"
            >
              <Plus className="w-5 h-5" />
              {isCreating ? 'Creating...' : 'Create Family'}
            </button>
          ) : (
            <Link
              to="/family/settings"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-sage text-white rounded-xl hover:bg-sage-dark transition-colors"
            >
              <Plus className="w-5 h-5" />
              Create Family
            </Link>
          )}

          <Link
            to="/family/dashboard"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-warm-gray/20 text-navy rounded-xl hover:bg-cream transition-colors"
          >
            <Home className="w-5 h-5" />
            Go to Dashboard
          </Link>
        </div>

        {isAdmin && (
          <p className="mt-6 text-sm text-warm-gray">
            <AlertCircle className="w-4 h-4 inline-block mr-1" />
            Admin users can access all portals but may need associated data for some features.
          </p>
        )}
      </div>
    </div>
  );
}

export default NoFamilyState;
